import { z } from 'zod';
import { createFixture } from 'zod-fixture';
import type { Merge } from 'simplytyped';
import type { TProductFeeData } from './ProductFee';
import { ProductFeeDataSchema } from './ProductFee';

const MerchantIdSchema = z
  .string({
    required_error: 'merchantId is required'
  })
  .refine(
    (value: string) => /^EP\d{5}/.test(value),
    (val) => ({
      message: `Not a valid MerchantId. Please use the format \`EP00001\`. You passed "${val}"`
    })
  );

export const ProductSchema = z.object({
  id: z.string({
    required_error: 'id is required',
    invalid_type_error: 'id must be a string'
  }),
  productCatalogueId: z.string({
    required_error: 'productCatalogueId is required',
    invalid_type_error: 'productCatalogueId must be a string'
  }),
  merchantId: MerchantIdSchema,
  productType: z.string({
    required_error: 'productType is required',
    invalid_type_error: 'productType must be a string'
  }),
  status: z.string({
    required_error: 'status is required',
    invalid_type_error: 'status must be a string'
  }),
  snapshotId: z
    .string({
      required_error: 'snapshotId is required',
      invalid_type_error: 'snapshotId must be a string'
    })
    .optional(),
  availableTerms: z
    .array(
      z.coerce
        .number({
          required_error: 'Loan Terms is required'
        })
        .int()
        .nonnegative({
          message: 'Loan Terms must be a positive number'
        })
    )
    .nonempty({
      message: 'Loan Terms must have at least one term defined'
    }),
  maxLoanAmount: z.coerce
    .number({
      required_error: 'maxLoanAmount is required',
      invalid_type_error: 'maxLoanAmount must be a number'
    })
    .step(0.01, `maxLoanAmount must be a multiple of 0.01`)
    .nonnegative(),
  minBureauScore: z
    .number({
      required_error: 'minBureauScore is required',
      invalid_type_error: 'minBureauScore must be a number'
    })
    .int()
    .nonnegative()
    .optional(),
  productArchived: z.boolean({
    required_error: 'productArchived is required',
    invalid_type_error: 'productArchived must be a boolean'
  }),
  feeSnapshotId: z.string({
    required_error: 'feeSnapshotId is required',
    invalid_type_error: 'feeSnapshotId must be a string'
  }),
  minLoanAmount: z.coerce
    .number({
      required_error: 'minLoanAmount is required',
      invalid_type_error: 'minLoanAmount must be a number'
    })
    .step(0.01, `minLoanAmount must be a multiple of 0.01`)
    .nonnegative(),
  // fees are optional and only returned if ?loadFees=true provided
  fees: z.record(z.string(), ProductFeeDataSchema).optional(),
  feeMatrix: z
    .object({
      termRanges: z.array(
        z
          .object({
            min: z.coerce
              .number()
              .step(0.01, `Term Range Minimum must be a multiple of 0.01`)
              .nonnegative(),
            max: z.coerce
              .number()
              .step(0.01, `Term Range Maximum must be a multiple of 0.01`)
              .refine((max) => max > 0 || max === -1, {
                message:
                  'Loan Amount range max must be greater than zero, or negative one (unlimited)'
              })
          })
          .refine((data) => data.max >= data.min, {
            message: `Term Range Max should be greater than or equal to the minimum`,
            path: ['max']
          })
      ),
      feeBands: z.array(
        z.object({
          loanAmountRange: z
            .object({
              min: z.coerce
                .number()
                .step(0.01, `Loan Amount Minimum must be a multiple of 0.01`)
                .nonnegative(),
              max: z.coerce
                .number()
                .step(0.01, `Loan Amount Maximum must be a multiple of 0.01`)
                .refine((max) => max > 0 || max === -1, {
                  message:
                    'Loan Amount range max must be greater than zero, or negative one (unlimited)'
                })
            })
            .refine((data) => data.max >= data.min, {
              message: `Loan Amount Range max should be greater than or equal to min`,
              path: ['max']
            }),
          termFees: z.array(
            z.coerce
              .number()
              .step(0.01, `Term Fee must be a multiple of 0.01`)
              .nonnegative()
          )
        })
      )
    })
    .optional()
});

const product = createFixture(
  ProductSchema,
  { seed: 4 } // change seed to generate a new product
);

export type TProduct = ReturnType<typeof ProductSchema.parse>;

export type TAccountProduct = Merge<
  TProduct,
  {
    fees: Record<string, TProductFeeData>;
  }
>;
